import { useEffect, useImperativeHandle, useRef, useState } from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { TimePicker } from "./TimePicker";
import {
  type TimePickerDataProps,
  timePickerDataSchema,
} from "./TimePicker.schema";
import type { TimeRangeValue } from "./TimePicker.types";
import { TimePickerContent } from "./TimePickerContent";
import { TimePickerTrigger } from "./TimePickerTrigger";
import { TimePickerValue } from "./TimePickerValue";
import { TIME_PICKER_TAG_NAME } from "./constants";

export interface TimePickerHandle {
  value: (value: string | null) => void;
  disabled: (value?: boolean) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [TIME_PICKER_TAG_NAME]: {
      props: TimePickerDataProps;
      handle: TimePickerHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-time-picker": HTMLElement & TimePickerHandle;
  }
}

export function RWCTimePicker(
  props: TimePickerDataProps & { ref?: Ref<TimePickerHandle> },
) {
  return props.range ? (
    <RWCTimeRangePicker {...props} />
  ) : (
    <RWCSingleTimePicker {...props} />
  );
}

function RWCSingleTimePicker({
  ref,
  value: initialValue,
  disabled: initialDisabled,
  placeholder,
  minuteStep,
  arrow,
  size,
  layout,
}: TimePickerDataProps & { ref?: Ref<TimePickerHandle> }) {
  const host = useIslandHost();

  const [value, setValue] = useState<string | null>(initialValue ?? null);
  const [disabled, setDisabled] = useState(initialDisabled ?? false);

  useImperativeHandle(
    ref,
    () => ({
      value: setValue,
      disabled: (nextDisabled = true) => setDisabled(nextDisabled),
    }),
    [],
  );

  useEffect(() => {
    getElementInternals(host)?.setFormValue(disabled ? null : value);
  }, [host, value, disabled]);

  function handleValueChange(next: string | null) {
    setValue(next);

    if (next) {
      host?.setAttribute("value", next);
    } else {
      host?.removeAttribute("value");
    }

    host?.dispatchEvent(
      new CustomEvent("time-picker:change", {
        detail: { value: next },
        bubbles: true,
      }),
    );
  }

  return (
    <TimePicker
      value={value}
      onValueChange={handleValueChange}
      disabled={disabled}
      minuteStep={minuteStep}
      arrow={arrow}
      size={size}
      layout={layout}
    >
      <TimePickerTrigger>
        <TimePickerValue placeholder={placeholder} />
      </TimePickerTrigger>
      <TimePickerContent />
    </TimePicker>
  );
}

// Both ends go out together, once the popup closes on a complete range
function RWCTimeRangePicker({
  ref,
  start,
  end,
  disabled: initialDisabled,
  placeholder,
  minuteStep,
  arrow,
  size,
  layout,
}: TimePickerDataProps & { ref?: Ref<TimePickerHandle> }) {
  const host = useIslandHost();

  const [value, setValue] = useState<TimeRangeValue>([
    start ?? null,
    end ?? null,
  ]);
  const [disabled, setDisabled] = useState(initialDisabled ?? false);
  const changed = useRef(false);

  useImperativeHandle(
    ref,
    () => ({
      value: (next) => {
        const [nextStart = null, nextEnd = null] = next?.split("-") ?? [];

        setValue([nextStart, nextEnd]);
      },
      disabled: (nextDisabled = true) => setDisabled(nextDisabled),
    }),
    [],
  );

  const [startValue, endValue] = value;
  const formValue = startValue && endValue ? `${startValue}-${endValue}` : null;

  useEffect(() => {
    getElementInternals(host)?.setFormValue(disabled ? null : formValue);
  }, [host, formValue, disabled]);

  function handleValueChange(next: TimeRangeValue) {
    setValue(next);
    changed.current = true;

    for (const [name, time] of [
      ["start", next[0]],
      ["end", next[1]],
    ] as const) {
      if (time) {
        host?.setAttribute(name, time);
      } else {
        host?.removeAttribute(name);
      }
    }
  }

  function handleOpenChangeComplete(open: boolean) {
    if (open || !changed.current || !startValue || !endValue) {
      return;
    }

    changed.current = false;

    host?.dispatchEvent(
      new CustomEvent("time-picker:change", {
        detail: { value: [startValue, endValue] },
        bubbles: true,
      }),
    );
  }

  return (
    <TimePicker
      range
      value={value}
      onValueChange={handleValueChange}
      onOpenChangeComplete={handleOpenChangeComplete}
      disabled={disabled}
      minuteStep={minuteStep}
      arrow={arrow}
      size={size}
      layout={layout}
    >
      <TimePickerTrigger>
        <TimePickerValue placeholder={placeholder} />
      </TimePickerTrigger>
      <TimePickerContent />
    </TimePicker>
  );
}

componentManager.register(
  rwc(TIME_PICKER_TAG_NAME, {
    component: RWCTimePicker,
    dataSchema: timePickerDataSchema,
    formAssociated: true,
  }),
);
